import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  XCircle, 
  Eye, 
  DollarSign,
  Package,
  User,
  Bell
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import { format } from 'date-fns';
import { generateOrderXML } from '../../utils/xmlGenerator';

const PendingOrdersWidget = ({ maxOrders = 5, linkPath = "/staff-area/approvals" }) => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [actionType, setActionType] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchPendingOrders();
    
    // Real-time subscription
    const subscription = supabase
      .channel('pending_orders_widget')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: 'internal_status=eq.pending_approval'
        },
        () => {
          fetchPendingOrders();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPendingOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          order_id,
          order_number,
          cart_items,
          created_at,
          order_instructions,
          allow_partial_delivery,
          delivery_type,
          freight_carrier,
          freight_send_via,
          freight_account,
          shipping_instructions,
          accounts:user_id (
            id,
            company_name,
            full_name,
            email
          ),
          delivery_addresses!delivery_address_id (
            address_line1,
            address_line2,
            city,
            state,
            postal_code,
            site_code,
            contact_name,
            contact_phone,
            contact_email,
            contact_mobile
          )
        `)
        .eq('internal_status', 'pending_approval')
        .order('created_at', { ascending: false })
        .limit(maxOrders);

      if (error) throw error;
      setPendingOrders(data || []);
    } catch (error) {
      console.error('Error fetching pending orders:', error);
      showError(error, 'Failed to fetch pending orders');
    } finally {
      setLoading(false);
    }
  };

  const calculateOrderTotal = (cartItems) => {
    if (!Array.isArray(cartItems)) return 0;
    return cartItems.reduce((sum, item) => {
      return sum + (parseFloat(item.price || 0) * parseInt(item.quantity || 0));
    }, 0);
  };

  const handleQuickApprove = async (order) => {
    setApproving(prev => ({ ...prev, [order.id]: true }));
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get account data for folder structure and XML generation
      const { data: accountData, error: accountError } = await supabase
        .from('accounts')
        .select('deb_id')
        .eq('id', order.accounts.id)
        .single();
      
      if (accountError) throw accountError;
      if (!accountData.deb_id) throw new Error('Missing debtor ID for this account');

      // Generate XML for the order
      const xml = generateOrderXML(order, accountData.deb_id);
      
      // Create folder structure with deb_id
      const debId = accountData.deb_id || 'unknown';
      const fileName = `${debId}/order_${Date.now()}_approved.xml`;
      
      // Store XML in Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('orders')
        .upload(fileName, xml, {
          contentType: 'application/xml',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get the public URL of the uploaded file
      const { data: { publicUrl } } = supabase.storage
        .from('orders')
        .getPublicUrl(fileName);

      // Approve the order using RPC function
      const { data: functionResult, error: functionError } = await supabase
        .rpc('approve_order', {
          order_id_param: order.id,
          staff_user_id: user.id,
          approval_notes: null,
          updated_cart_items: null
        });

      if (functionError) throw functionError;

      if (!functionResult.success) {
        throw new Error(functionResult.error || 'Failed to approve order');
      }

      // Update the order with XML details
      const { error: updateError } = await supabase
        .from('orders')
        .update({
          xml_url: publicUrl,
          xml_generated_at: new Date().toISOString()
        })
        .eq('id', order.id);

      if (updateError) throw updateError;

      showNotification(`Order #${order.order_number} approved successfully`, 'success');
      fetchPendingOrders();

    } catch (error) {
      console.error('Error approving order:', error);
      showError(error, 'Failed to approve order');
    } finally {
      setApproving(prev => ({ ...prev, [order.id]: false }));
    }
  };

  const openActionModal = (order, action) => {
    setSelectedOrder(order);
    setActionType(action);
    setShowModal(true);
  };

  const confirmAction = async () => {
    if (!selectedOrder) return;

    setApproving(prev => ({ ...prev, [selectedOrder.id]: true }));
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      if (actionType === 'approve') {
        // Get account data for folder structure and XML generation
        const { data: accountData, error: accountError } = await supabase
          .from('accounts')
          .select('deb_id')
          .eq('id', selectedOrder.accounts.id)
          .single();
        
        if (accountError) throw accountError;
        if (!accountData.deb_id) throw new Error('Missing debtor ID for this account');

        // Generate XML for the order
        const xml = generateOrderXML(selectedOrder, accountData.deb_id);
        
        // Create folder structure with deb_id
        const debId = accountData.deb_id || 'unknown';
        const fileName = `${debId}/order_${Date.now()}_approved.xml`;
        
        // Store XML in Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('orders')
          .upload(fileName, xml, {
            contentType: 'application/xml',
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Get the public URL of the uploaded file
        const { data: { publicUrl } } = supabase.storage
          .from('orders')
          .getPublicUrl(fileName);

        // Approve the order using RPC function
        const { data: functionResult, error: functionError } = await supabase
          .rpc('approve_order', {
            order_id_param: selectedOrder.id,
            staff_user_id: user.id,
            approval_notes: approvalNotes || null,
            updated_cart_items: null
          });

        if (functionError) throw functionError;
        if (!functionResult.success) {
          throw new Error(functionResult.error || 'Failed to approve order');
        }

        // Update the order with XML details
        const { error: updateError } = await supabase
          .from('orders')
          .update({
            xml_url: publicUrl,
            xml_generated_at: new Date().toISOString()
          })
          .eq('id', selectedOrder.id);

        if (updateError) throw updateError;

        showNotification(`Order #${selectedOrder.order_number} approved successfully`, 'success');
      } else if (actionType === 'reject') {
        if (!rejectionReason.trim()) {
          showError(new Error('Rejection reason is required'), 'Please provide a reason');
          return;
        }

        const { data: functionResult, error: functionError } = await supabase
          .rpc('reject_order', {
            order_id_param: selectedOrder.id,
            staff_user_id: user.id,
            rejection_reason_param: rejectionReason
          });

        if (functionError) throw functionError;
        if (!functionResult.success) {
          throw new Error(functionResult.error || 'Failed to reject order');
        }

        showNotification(`Order #${selectedOrder.order_number} rejected`, 'success');
      }

      closeModal();
      fetchPendingOrders();

    } catch (error) {
      console.error('Error processing order:', error);
      showError(error, `Failed to ${actionType} order`);
    } finally {
      setApproving(prev => ({ ...prev, [selectedOrder.id]: false }));
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
    setActionType('');
    setApprovalNotes('');
    setRejectionReason('');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="h-6 bg-gray-200 rounded w-48"></div>
            <div className="h-5 bg-gray-200 rounded w-20"></div>
          </div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-200 bg-red-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Bell className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-semibold text-red-900">Pending Order Approvals</h3>
            </div>
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {pendingOrders.length} pending
              </span>
              {pendingOrders.length > 0 && (
                <a
                  href={linkPath}
                  className="text-sm text-red-600 hover:text-red-800 font-medium"
                >
                  View All →
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
          {pendingOrders.length === 0 ? (
            <div className="p-6 text-center">
              <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-3" />
              <h4 className="text-sm font-medium text-gray-900">No pending approvals</h4>
              <p className="text-sm text-gray-500">All orders have been processed</p>
            </div>
          ) : (
            pendingOrders.map((order) => {
              const orderTotal = calculateOrderTotal(order.cart_items);
              const isProcessing = approving[order.id];
              
              return (
                <div key={order.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-900">
                          Order #{order.order_number}
                        </h4>
                        <span className="text-xs text-gray-500">
                          {format(new Date(order.created_at), 'MMM dd, HH:mm')}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-4 text-xs text-gray-600 mb-2">
                        <div className="flex items-center space-x-1">
                          <User className="w-3 h-3" />
                          <span className="truncate max-w-32">
                            {order.accounts?.company_name || order.accounts?.full_name}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Package className="w-3 h-3" />
                          <span>{order.cart_items?.length || 0} items</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <DollarSign className="w-3 h-3" />
                          <span className="font-medium text-green-600">${orderTotal.toFixed(2)}</span>
                        </div>
                      </div>

                      {/* Order Items Preview */}
                      <div className="text-xs text-gray-500 mb-3">
                        {order.cart_items?.slice(0, 2).map((item, idx) => (
                          <div key={idx} className="truncate">
                            {item.partNumber} - {item.description} (Qty: {item.quantity})
                          </div>
                        ))}
                        {order.cart_items?.length > 2 && (
                          <div className="text-gray-400">
                            +{order.cart_items.length - 2} more items...
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-2 mt-3">
                    <button
                      onClick={() => handleQuickApprove(order)}
                      disabled={isProcessing}
                      className="flex-1 inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded border border-transparent bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {isProcessing ? 'Approving...' : 'Quick Approve'}
                    </button>
                    <button
                      onClick={() => openActionModal(order, 'reject')}
                      disabled={isProcessing}
                      className="flex-1 inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded border border-transparent bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      <XCircle className="w-3 h-3 mr-1" />
                      Reject
                    </button>
                    <button
                      onClick={() => openActionModal(order, 'view')}
                      className="px-2 py-1 text-xs font-medium rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <Eye className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </motion.div>

      {/* Action Modal */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={closeModal}></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Order #{selectedOrder.order_number} - {actionType === 'view' ? 'Details' : actionType === 'approve' ? 'Approve' : 'Reject'}
                  </h3>
                  <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>

                <div className="max-h-96 overflow-y-auto mb-4">
                  {/* Customer Info */}
                  <div className="mb-4 p-3 bg-gray-50 rounded">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm"><strong>Company:</strong> {selectedOrder.accounts?.company_name || selectedOrder.accounts?.full_name}</p>
                        <p className="text-sm"><strong>Email:</strong> {selectedOrder.accounts?.email}</p>
                      </div>
                      <div>
                        <p className="text-sm"><strong>Order Date:</strong> {format(new Date(selectedOrder.created_at), 'PPP')}</p>
                        <p className="text-sm"><strong>Total:</strong> ${calculateOrderTotal(selectedOrder.cart_items).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Items Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedOrder.cart_items?.map((item, index) => (
                          <tr key={index}>
                            <td className="px-3 py-2 text-sm text-gray-900">{item.partNumber}</td>
                            <td className="px-3 py-2 text-sm text-gray-900">{item.description}</td>
                            <td className="px-3 py-2 text-sm text-gray-900 text-center">{item.quantity}</td>
                            <td className="px-3 py-2 text-sm text-gray-900 text-right">${parseFloat(item.price || 0).toFixed(2)}</td>
                            <td className="px-3 py-2 text-sm text-gray-900 text-right">${(parseFloat(item.price || 0) * parseInt(item.quantity || 0)).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Action Forms */}
                  {actionType === 'approve' && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Approval Notes (Optional)
                      </label>
                      <textarea
                        value={approvalNotes}
                        onChange={(e) => setApprovalNotes(e.target.value)}
                        rows="2"
                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500"
                        placeholder="Add any notes..."
                      />
                    </div>
                  )}

                  {actionType === 'reject' && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rejection Reason <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        rows="2"
                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500"
                        placeholder="Please provide a reason..."
                        required
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                {actionType === 'approve' && (
                  <button
                    onClick={confirmAction}
                    disabled={approving[selectedOrder.id]}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {approving[selectedOrder.id] ? 'Approving...' : 'Approve Order'}
                  </button>
                )}
                
                {actionType === 'reject' && (
                  <button
                    onClick={confirmAction}
                    disabled={approving[selectedOrder.id] || !rejectionReason.trim()}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {approving[selectedOrder.id] ? 'Rejecting...' : 'Reject Order'}
                  </button>
                )}

                <button
                  onClick={closeModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {actionType === 'view' ? 'Close' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PendingOrdersWidget;
